// src/components/WiggiWiggiGame/Unit.js
import React from 'react';
// Import STATUS_PLAYING is NOT needed here anymore
import { UNIT_TYPES, OWNER_PLAYER, OWNER_COMPUTER, OWNER_NATURE, CENTER_HEX_ID } from './constants';

// gameStatus prop is NOT needed here anymore
const Unit = ({ unitData, hexId, hexWidth, hexHeight, hexSize, isVisible, isExploding }) => {
    // Basic validation and visibility checks
    if (!unitData || typeof unitData !== 'object') { return null; }
    const isIdol = unitData.type === 'IDOL_STONE';

    // CORRECTED VISIBILITY CHECK:
    // The isVisible prop already accounts for gameStatus (calculated in GameBoard.js)
    // Don't render non-idols if they aren't marked as visible by the parent
    if (!isVisible && !isIdol) {
        return null;
    }
    // END CORRECTION

    const unitStats = UNIT_TYPES[unitData.type];
    if (!unitStats) {
        console.warn("Unit Component: No stats for type:", unitData.type);
        return null;
    }

    // Handle Explosion Rendering State
     if (isExploding) {
         // Calculate center position for explosion icon
         const [explCol, explRow] = hexId.split(',').map(Number);
         const explHexX = explCol * hexWidth * 0.75;
         const explHexY = explRow * hexHeight + (explCol % 2 === 1 ? hexHeight * 0.5 : 0);
         const explAngles = Array(6).fill(0).map((_, i) => (Math.PI / 3) * i);
         const explVertices = explAngles.map(angle => ({ x: explHexX + hexSize * Math.cos(angle), y: explHexY + hexSize * Math.sin(angle) }));
         const explCenterX = explVertices.reduce((sum, v) => sum + v.x, 0) / 6;
         const explCenterY = explVertices.reduce((sum, v) => sum + v.y, 0) / 6;
         const explRadius = hexSize * 0.7;
         const explosionDisplayName = unitData.owner === OWNER_PLAYER ? "Player Explosion" : "Computer Explosion";

         return (
             <g key={`explosion-${hexId}`} pointerEvents="none">
                 <title>{explosionDisplayName} ({unitData.type})</title>
                 <text x={explCenterX} y={explCenterY} textAnchor="middle" dominantBaseline="middle" fill="#FF0000" fontSize={explRadius * 1.6} fontWeight="bold">💥</text>
             </g>
         );
     }

    // --- Calculate Geometry for Standard Unit ---
    const [col, row] = hexId.split(',').map(Number);
    const angles = Array(6).fill(0).map((_, i) => (Math.PI / 3) * i);
    const hexX = col * hexWidth * 0.75;
    const hexY = row * hexHeight + (col % 2 === 1 ? hexHeight * 0.5 : 0);
    const vertices = angles.map(angle => ({ x: hexX + hexSize * Math.cos(angle), y: hexY + hexSize * Math.sin(angle) }));
    const centerX = vertices.reduce((sum, v) => sum + v.x, 0) / 6;
    const centerY = vertices.reduce((sum, v) => sum + v.y, 0) / 6;
    const unitRadius = hexSize * 0.7;

    // --- Determine Styling & Base Info ---
    let fillColor = 'transparent';
    let strokeColor = 'none';
    let icon = unitStats.icon || '?';
    let unitDisplayName = unitStats.name;
    let health = unitData.health;
    let showProgressBar = false; // Flag to determine if ANY bar should show
    let barPercent = 0;
    let barColor = '#48bb78'; // Default Green

    // Base styling per owner
    if (unitData.owner === OWNER_PLAYER) { fillColor = '#bfdbfe'; strokeColor = '#333'; }
    else if (unitData.owner === OWNER_COMPUTER) { fillColor = '#fecaca'; strokeColor = '#333'; }
    else if (unitData.owner === OWNER_NATURE) { fillColor = 'transparent'; strokeColor = 'none'; }

    // Idol specific icon override
    if (isIdol) { icon = (hexId === CENTER_HEX_ID) ? UNIT_TYPES.BANANA_TREE.icon : unitStats.icon; }

    // --- Determine Bar Logic (Health vs. Lifespan) ---
    if (unitData.type === 'COCONUT_BOMBER' && unitData.isActivated) {
        // --- Activated Bomber: Show Lifespan Bar ---
        showProgressBar = true;
        const maxLifespan = unitStats.maxLifespan || 10;
        const currentLifespan = unitData.lifespanRemaining || 0;
        barPercent = (maxLifespan > 0)
            ? Math.max(0, Math.min(100, (currentLifespan / maxLifespan) * 100))
            : 0;
        unitDisplayName += ` (Lifespan: ${currentLifespan}/${maxLifespan})`;
        if (barPercent <= 25) barColor = '#e53e3e';
        else if (barPercent <= 50) barColor = '#ed8936';
        else barColor = '#48bb78';

    } else {
        // --- Other Units OR Unactivated Bomber: Show Health Bar (if applicable) ---
        const maxHealth = unitStats.health || 0;
        showProgressBar = maxHealth > 1 && !isIdol;
        if (showProgressBar) {
            barPercent = (maxHealth > 0)
                ? Math.max(0, Math.min(100, (health / maxHealth) * 100))
                : 0;
             unitDisplayName += ` (${health}/${maxHealth} HP)`;
            if (barPercent <= 25) barColor = '#e53e3e';
            else if (barPercent <= 50) barColor = '#ed8936';
            else barColor = '#48bb78';
        } else if (!isIdol && maxHealth > 0) {
             unitDisplayName += ` (${health}/${maxHealth} HP)`;
        }
    }

    // --- Render SVG ---
    return (
        <g key={`unit-${unitData.id || hexId}`} pointerEvents="none">
            <title>{unitDisplayName}</title>
            {!isIdol && fillColor !== 'transparent' && (
                 <circle cx={centerX} cy={centerY} r={unitRadius} fill={fillColor} stroke={strokeColor} strokeWidth="1.5" />
            )}
            <text x={centerX} y={centerY} textAnchor="middle" dominantBaseline="middle" fill="#000" fontSize={unitRadius * (isIdol ? 1.0 : 0.8)} fontWeight="bold">
                 {icon}
            </text>
            {showProgressBar && (
                <>
                    <rect x={centerX - unitRadius * 0.7} y={centerY + unitRadius * 0.8} width={unitRadius * 1.4} height={unitRadius * 0.2} fill="#555" rx="2"/>
                    <rect
                        x={centerX - unitRadius * 0.7}
                        y={centerY + unitRadius * 0.8}
                        width={unitRadius * 1.4 * (barPercent / 100)}
                        height={unitRadius * 0.2}
                        fill={barColor}
                        rx="2"
                    />
                </>
            )}
        </g>
    );
};

export default Unit;